import { useState, useRef, useMemo, useCallback } from 'react';

import orderBy from 'lodash/orderBy';

import { EnhancedItem } from '../hooks/useEnhancedItems';
import { sortSelectedItemsToTheTop } from './utils/sortingHelpers';

type UseSortFreezeParams<Item> = {
  items: EnhancedItem<Item>[];
  enabled: boolean;
  selectedCount: number;
};

type UseSortFreezeResult<Item> = {
  sortedItems: EnhancedItem<Item>[];
  triggerSort: () => void;
};

/**
 * Hook to manage sort freezing behavior for dropdown items.
 * When enabled, sorts selected items to the top and maintains their order
 * during the menu session (while menu is open and user is searching).
 */
export const useSortFreeze = <Item>({
  items,
  enabled,
  selectedCount,
}: UseSortFreezeParams<Item>): UseSortFreezeResult<Item> => {
  // Track if menu has been opened at least once
  const hasOpenedOnceRef = useRef(false);

  // Increment to trigger re-sort (when menu opens or custom item is added)
  const [sortSessionId, setSortSessionId] = useState(0);

  // Cache sorted order during session
  const cachedSortedOrderRef = useRef<EnhancedItem<Item>[] | null>(null);

  // Track which session ID we last sorted for
  const lastSortedSessionIdRef = useRef(-1);

  const triggerSort = useCallback(() => {
    hasOpenedOnceRef.current = true;
    setSortSessionId((prev) => prev + 1);
  }, []);

  const sortedItems = useMemo(() => {
    if (!enabled) {
      return items;
    }

    // Conditional sorting based on menu session state
    const isFirstOpenWithInitialValues =
      !hasOpenedOnceRef.current && selectedCount > 0;
    const sessionIdChanged = sortSessionId !== lastSortedSessionIdRef.current;

    // Sort and cache if:
    // 1. First open with initial values (show pre-selected items at top)
    // 2. sortSessionId changed (menu opened or custom item added)
    if (isFirstOpenWithInitialValues || sessionIdChanged) {
      const sorted = sortSelectedItemsToTheTop(items);
      // Cache the sorted order in ref (updating ref during render is allowed for caching)
      cachedSortedOrderRef.current = sorted;
      lastSortedSessionIdRef.current = sortSessionId;
      return sorted;
    }

    // During menu session (using cached order), maintain frozen order
    // Map current filtered items to frozen order to maintain position
    const cachedOrder = cachedSortedOrderRef.current;
    if (!cachedOrder) {
      // Fallback: if no cached order, just return unsorted
      return items;
    }

    const frozenOrderMap = new Map(
      cachedOrder.map((item, index) => [item.value, { item, index }])
    );

    // Return items in frozen order, filtering to only show current filtered items
    return orderBy(
      items.map((item) => {
        const frozenData = frozenOrderMap.get(item.value);
        return frozenData ? { ...frozenData.item, ...item } : item;
      }),
      (item) => frozenOrderMap.get(item.value)?.index ?? Infinity
    );
  }, [items, enabled, sortSessionId, selectedCount]);

  return { sortedItems, triggerSort };
};
